import React, { useState, Fragment } from 'react';
import { Check, ChevronLeft, ChevronRight, UploadCloud } from 'lucide-react';
import { PageHero } from '../components/PageHero';
const STEPS = [{
  id: 1,
  label: 'Company Info'
}, {
  id: 2,
  label: 'Business Classification'
}, {
  id: 3,
  label: 'Financial & Capacity'
}, {
  id: 4,
  label: 'Documents'
}, {
  id: 5,
  label: 'Review & Submit'
}];
const inputClass = 'w-full rounded border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-au-green focus:border-transparent';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
function Field({
  id,
  label,
  required,
  children
}: {
  id: string;
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return <div data-id="element-343">
      <label htmlFor={id} className={labelClass} data-id="element-344">
        {label} {required && <span className="text-au-red" data-id="element-345">*</span>}
      </label>
      {children}
    </div>;
}
export function SupplierRegistrationPage() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const next = () => setStep(s => Math.min(s + 1, STEPS.length));
  const back = () => setStep(s => Math.max(s - 1, 1));
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
  }
  return <>
      <PageHero breadcrumb="Supplier Registration" title="Supplier Registration" subtitle="Register your company to participate in AU procurement opportunities." data-id="element-346" />

      <section className="py-16 md:py-20 bg-gray-50" data-id="element-347">
        <div className="max-w-4xl mx-auto px-4 md:px-8" data-id="element-348">
          {/* Stepper */}
          <div className="flex items-start justify-between mb-12" data-id="element-349">
            {STEPS.map((s, index) => {
            const completed = s.id < step;
            const active = s.id === step;
            return <Fragment key={s.id} data-id="element-350">
                  <div className="flex flex-col items-center text-center flex-shrink-0 w-20 md:w-28" data-id="element-351">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${completed ? 'bg-au-green text-white' : active ? 'bg-au-green text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-352">
                      {completed ? <Check size={18} data-id="element-353" /> : s.id}
                    </div>
                    <span className={`mt-2 text-xs leading-tight ${active ? 'text-au-green font-semibold' : 'text-gray-500'}`} data-id="element-354">
                      {s.label}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && <div className={`flex-1 h-0.5 mt-5 ${s.id < step ? 'bg-au-green' : 'bg-gray-200'}`} data-id="element-355" />}
                </Fragment>;
          })}
          </div>

          {/* Form card */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-100 shadow-sm p-8 md:p-10" data-id="element-356">
            {submitted ? <div className="text-center py-12" data-id="element-357">
                <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5" data-id="element-358">
                  <Check size={28} className="text-au-green" data-id="element-359" />
                </div>
                <h2 className="text-2xl font-serif font-bold text-au-green mb-2" data-id="element-360">
                  Registration Submitted
                </h2>
                <p className="text-gray-600 max-w-md mx-auto" data-id="element-361">
                  Thank you for registering. Our procurement team will review
                  your application and contact you regarding eligibility for
                  upcoming opportunities.
                </p>
              </div> : <>
                {/* Step 1 — Company Info */}
                {step === 1 && <div data-id="element-362">
                    <h2 className="text-2xl font-serif font-bold text-au-navy mb-6" data-id="element-363">
                      Company Information
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-364">
                      <Field id="companyName" label="Company Name" required data-id="element-365">
                        <input id="companyName" type="text" placeholder="Enter company name" className={inputClass} data-id="element-366" />
                      </Field>
                      <Field id="regNumber" label="Registration Number" required data-id="element-367">
                        <input id="regNumber" type="text" placeholder="Company registration number" className={inputClass} data-id="element-368" />
                      </Field>
                      <Field id="country" label="Country" required data-id="element-369">
                        <select id="country" className={inputClass} defaultValue="" data-id="element-370">
                          <option value="" disabled data-id="element-371">
                            Select country
                          </option>
                          <option data-id="element-372">Ethiopia</option>
                          <option data-id="element-373">Kenya</option>
                          <option data-id="element-374">Nigeria</option>
                          <option data-id="element-375">South Africa</option>
                          <option data-id="element-376">Ghana</option>
                          <option data-id="element-377">United Kingdom</option>
                          <option data-id="element-378">Other</option>
                        </select>
                      </Field>
                      <Field id="city" label="City" required data-id="element-379">
                        <input id="city" type="text" placeholder="City" className={inputClass} data-id="element-380" />
                      </Field>
                      <div className="md:col-span-2" data-id="element-381">
                        <Field id="address" label="Address" required data-id="element-382">
                          <input id="address" type="text" placeholder="Street address" className={inputClass} data-id="element-383" />
                        </Field>
                      </div>
                      <Field id="postalCode" label="Postal Code" required data-id="element-384">
                        <input id="postalCode" type="text" placeholder="Postal code" className={inputClass} data-id="element-385" />
                      </Field>
                      <Field id="website" label="Website" data-id="element-386">
                        <input id="website" type="url" placeholder="https://" className={inputClass} data-id="element-387" />
                      </Field>
                    </div>
                  </div>}

                {/* Step 2 — Business Classification */}
                {step === 2 && <div data-id="element-388">
                    <h2 className="text-2xl font-serif font-bold text-au-navy mb-6" data-id="element-389">
                      Business Classification
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-390">
                      <Field id="businessType" label="Business Type" required data-id="element-391">
                        <select id="businessType" className={inputClass} defaultValue="" data-id="element-392">
                          <option value="" disabled data-id="element-393">
                            Select type
                          </option>
                          <option data-id="element-394">Sole Proprietorship</option>
                          <option data-id="element-395">Partnership</option>
                          <option data-id="element-396">Private Limited Company</option>
                          <option data-id="element-397">Public Limited Company</option>
                          <option data-id="element-398">Cooperative</option>
                        </select>
                      </Field>
                      <Field id="industry" label="Primary Industry" required data-id="element-399">
                        <select id="industry" className={inputClass} defaultValue="" data-id="element-400">
                          <option value="" disabled data-id="element-401">
                            Select industry
                          </option>
                          <option data-id="element-402">Goods Supply</option>
                          <option data-id="element-403">Construction & Works</option>
                          <option data-id="element-404">Consultancy Services</option>
                          <option data-id="element-405">IT & Technology</option>
                          <option data-id="element-406">Logistics</option>
                        </select>
                      </Field>
                      <Field id="yearEstablished" label="Year Established" data-id="element-407">
                        <input id="yearEstablished" type="number" placeholder="e.g. 2010" className={inputClass} data-id="element-408" />
                      </Field>
                      <Field id="employees" label="Number of Employees" data-id="element-409">
                        <input id="employees" type="number" placeholder="e.g. 50" className={inputClass} data-id="element-410" />
                      </Field>
                    </div>
                  </div>}

                {/* Step 3 — Financial & Capacity */}
                {step === 3 && <div data-id="element-411">
                    <h2 className="text-2xl font-serif font-bold text-au-navy mb-6" data-id="element-412">
                      Financial & Capacity
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-413">
                      <Field id="turnover" label="Annual Turnover (GBP)" data-id="element-414">
                        <input id="turnover" type="text" placeholder="e.g. £2,000,000" className={inputClass} data-id="element-415" />
                      </Field>
                      <Field id="bankName" label="Bank Name" data-id="element-416">
                        <input id="bankName" type="text" placeholder="Bank name" className={inputClass} data-id="element-417" />
                      </Field>
                      <Field id="taxId" label="Tax Identification Number" data-id="element-418">
                        <input id="taxId" type="text" placeholder="Tax ID" className={inputClass} data-id="element-419" />
                      </Field>
                      <Field id="largestContract" label="Largest Contract Value" data-id="element-420">
                        <input id="largestContract" type="text" placeholder="e.g. £500,000" className={inputClass} data-id="element-421" />
                      </Field>
                    </div>
                  </div>}

                {/* Step 4 — Documents */}
                {step === 4 && <div data-id="element-422">
                    <h2 className="text-2xl font-serif font-bold text-au-navy mb-6" data-id="element-423">
                      Supporting Documents
                    </h2>
                    <div className="space-y-4" data-id="element-424">
                      {['Certificate of Incorporation', 'Tax Clearance Certificate', 'Company Profile'].map(doc => <div key={doc} data-id="element-425">
                          <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-426">
                            {doc}
                          </p>
                          <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg px-4 py-6 cursor-pointer hover:border-au-green transition-colors text-gray-500" data-id="element-427">
                            <UploadCloud size={20} className="text-au-green" data-id="element-428" />
                            <span className="text-sm" data-id="element-429">
                              Click to upload (PDF, max 5MB)
                            </span>
                            <input type="file" className="hidden" accept=".pdf" data-id="element-430" />
                          </label>
                        </div>)}
                    </div>
                  </div>}

                {/* Step 5 — Review & Submit */}
                {step === 5 && <div data-id="element-431">
                    <h2 className="text-2xl font-serif font-bold text-au-navy mb-6" data-id="element-432">
                      Review & Submit
                    </h2>
                    <p className="text-gray-600 mb-6" data-id="element-433">
                      Please review the information you have provided across all
                      steps. By submitting, you confirm that the details are
                      accurate and that you agree to the African Union
                      Commission's procurement terms and code of conduct.
                    </p>
                    <label className="flex items-start gap-3 text-sm text-gray-700" data-id="element-434">
                      <input type="checkbox" required className="mt-1 accent-au-green" data-id="element-435" />
                      <span data-id="element-436">
                        I confirm the information provided is accurate and
                        complete.
                      </span>
                    </label>
                  </div>}

                {/* Navigation */}
                <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-100" data-id="element-437">
                  <button type="button" onClick={back} disabled={step === 1} className="inline-flex items-center text-gray-600 font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:text-au-navy transition-colors focus:outline-none focus:ring-2 focus:ring-au-green focus:ring-offset-2 rounded-sm px-2 py-1" data-id="element-438">
                    <ChevronLeft size={18} className="mr-1" data-id="element-439" /> Back
                  </button>

                  {step < STEPS.length ? <button type="button" onClick={next} className="inline-flex items-center bg-au-green text-white px-6 py-2.5 rounded font-semibold hover:bg-au-navy transition-colors focus:outline-none focus:ring-2 focus:ring-au-green focus:ring-offset-2" data-id="element-440">
                      Next <ChevronRight size={18} className="ml-1" data-id="element-441" />
                    </button> : <button type="submit" className="inline-flex items-center bg-au-green text-white px-6 py-2.5 rounded font-semibold hover:bg-au-navy transition-colors focus:outline-none focus:ring-2 focus:ring-au-green focus:ring-offset-2" data-id="element-442">
                      Submit Registration
                    </button>}
                </div>
              </>}
          </form>
        </div>
      </section>
    </>;
}